import React from "react";
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from "react-router";
import { connect } from "react-redux";
import { PropagatedProps } from "../layout/main/component";
import { asyncRender } from "./BaseRoute";
import ErrorBoundary from "../layout/errorBoundary";
import { importLazy, LazilyLoadFactory } from "../layout/lazilyload";
import { Config } from "../typings/interfaces";
import { AppState } from "../creators/reducers";

export interface ContainersRouteProps extends RouteComponentProps {
    ContainerNodes: React.ComponentType<PropagatedProps>;
    NodeDetail: React.ComponentType<PropagatedProps>;
    PodList: React.ComponentType<PropagatedProps>;
    PodDetail: React.ComponentType<PropagatedProps>;
    EventList: React.ComponentType<PropagatedProps>;
    Workloads: React.ComponentType<PropagatedProps>;
    WorkloadDetail: React.ComponentType<PropagatedProps>;
    DeployWorkload: React.ComponentType<PropagatedProps>;
    ConfigMaps: React.ComponentType<PropagatedProps>;
    ConfigMapDetail: React.ComponentType<PropagatedProps>;
    CreateConfigMap: React.ComponentType<PropagatedProps>;
    Secrets: React.ComponentType<PropagatedProps>;
    CreateSecret: React.ComponentType<PropagatedProps>;
    componentProps: PropagatedProps;
    config: Config;
}

export const ContainersRoute: React.FunctionComponent<ContainersRouteProps> = (props) => {
    const {
        ContainerNodes,
        NodeDetail,
        PodList,
        PodDetail,
        EventList,
        Workloads,
        WorkloadDetail,
        DeployWorkload,
        ConfigMaps,
        ConfigMapDetail,
        CreateConfigMap,
        Secrets,
        CreateSecret,
        componentProps,
        config,
    } = props;

    const asyncRenderWithProps = asyncRender(componentProps);

    return (
        <ErrorBoundary>
            <Switch>
                {config.featureToggle.containerManagement && (
                    <Route exact path="/containers/nodes" render={asyncRenderWithProps(ContainerNodes, "k8sNodes")} />
                )}
                {config.featureToggle.containerManagement && (
                    <Route path="/containers/nodes/:name" render={asyncRenderWithProps(NodeDetail, "k8sNode")} />
                )}
                {config.featureToggle.containerManagement && (
                    <Route exact path="/containers/pods" render={asyncRenderWithProps(PodList, "k8sPods")} />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        path="/containers/pods/:namespace/:name"
                        render={asyncRenderWithProps(PodDetail, "k8sPod")}
                    />
                )}
                {config.featureToggle.containerManagement && (
                    <Route exact path="/containers/events" render={asyncRenderWithProps(EventList, "k8sEvents")} />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        exact
                        path="/containers/workloads"
                        render={asyncRenderWithProps(Workloads, "k8sWorkloads")}
                    />
                )}
                {/* Before the :name route, or "deploy" would be read as a name. */}
                {config.featureToggle.containerManagement && (
                    <Route
                        exact
                        path="/containers/workloads/deploy"
                        render={asyncRenderWithProps(DeployWorkload, "k8sWorkloadCreate")}
                    />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        path="/containers/workloads/:name"
                        render={asyncRenderWithProps(WorkloadDetail, "k8sWorkload")}
                    />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        exact
                        path="/containers/config-maps"
                        render={asyncRenderWithProps(ConfigMaps, "k8sConfigMaps")}
                    />
                )}
                {/* Before the :name route, or "new" would be read as a name. */}
                {config.featureToggle.containerManagement && (
                    <Route
                        exact
                        path="/containers/config-maps/new"
                        render={asyncRenderWithProps(CreateConfigMap, "k8sConfigCreate")}
                    />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        path="/containers/config-maps/:name"
                        render={asyncRenderWithProps(ConfigMapDetail, "k8sConfigMap")}
                    />
                )}
                {config.featureToggle.containerManagement && (
                    <Route exact path="/containers/secrets" render={asyncRenderWithProps(Secrets, "k8sSecrets")} />
                )}
                {config.featureToggle.containerManagement && (
                    <Route
                        exact
                        path="/containers/secrets/new"
                        render={asyncRenderWithProps(CreateSecret, "k8sConfigCreate")}
                    />
                )}
                <Redirect exact from="/containers" to="/containers/nodes" />
            </Switch>
        </ErrorBoundary>
    );
};

const mapStateToProps = ({ config }: AppState) => ({ config });

export default LazilyLoadFactory(withRouter(connect(mapStateToProps)(ContainersRoute)), {
    ContainerNodes: () => importLazy(import("../features/containers/nodes/container")),
    NodeDetail: () => importLazy(import("../features/containers/nodes/detail/container")),
    PodList: () => importLazy(import("../features/containers/pods/container")),
    PodDetail: () => importLazy(import("../features/containers/pods/detail/container")),
    EventList: () => importLazy(import("../features/containers/events/container")),
    Workloads: () => importLazy(import("../features/containers/workloads/container")),
    WorkloadDetail: () => importLazy(import("../features/containers/workloads/detail/container")),
    DeployWorkload: () => importLazy(import("../features/containers/workloads/deploy/container")),
    ConfigMaps: () => importLazy(import("../features/containers/config/containers")),
    ConfigMapDetail: () =>
        importLazy(import("../features/containers/config/containers").then((m) => ({ default: m.ConfigMapDetail }))),
    CreateConfigMap: () =>
        importLazy(import("../features/containers/config/containers").then((m) => ({ default: m.CreateConfigMap }))),
    Secrets: () => importLazy(import("../features/containers/config/containers").then((m) => ({ default: m.Secrets }))),
    CreateSecret: () =>
        importLazy(import("../features/containers/config/containers").then((m) => ({ default: m.CreateSecret }))),
});
